/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { Type } from '@angular/core';
import { DatePipe } from '@angular/common';
import { isNil } from 'lodash';

/**
 * Combines different parts of a url into a normalized value, adding or
 * removing forward slashes as necessary.
 *
 * @param urlArray Array containing the parts to be combined into a URL
 */
function normalizeUrl(urlArray: string[]): string {
    let urlString = '';
    urlArray.forEach((elt, idx) => {
        if (!elt.startsWith('/') && idx !== 0) {
            urlString += '/';
        }
        if (elt.endsWith('/')) {
            urlString += elt.slice(0, elt.length - 1);
        } else {
            urlString += elt;
        }
    });
    return urlString;
}

/**
 * Converts an input value to lowercase string or empty.
 * Not locale sensitive.
 *
 * @param value string or falsy value
 */
function toLowerCaseOrEmpty(value: string | undefined | null): string {
    return (value && value.toLowerCase()) || '';
}

class CachedUuid {
    private static uuid = null;

    static getUuid(): string {
        return CachedUuid.uuid;
    }
    static refreshUuid(): void {
        CachedUuid.uuid = generateUuidInternal();
    }
}

CachedUuid.refreshUuid();

function generateUuidInternal(): string {
    let d = Date.now();
    // This is based on UUID generation logic
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c: string) {
        /* eslint-disable no-bitwise */
        const r = ((d + Math.random() * 16) % 16) | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        /* eslint-enable no-bitwise */
    });
    return uuid;
}

function generateUuid(useCache: boolean = false): string {
    if (useCache) {
        return CachedUuid.getUuid();
    }
    return generateUuidInternal();
}

function invalidPipeArgumentError(type: Type<any>, value: Object) {
    return Error(`InvalidPipeArgument: '${value}' for pipe '${type.toString()}'`);
}

function getString(value: any, limitLengthTo?: number): string {
    let ret = value && typeof value !== 'string' ? JSON.stringify(value) : value || '';
    if (limitLengthTo) {
        ret = ret.substr(0, limitLengthTo);
    }
    return ret;
}
/**
 *
 * @param error - Http error response
 * @returns whether the failure occurred due to timeout (-100) or connection issue (0)
 */
function isTimeoutOrConnectivityError(error): boolean {
    return !isNil(error) && !isNil(error.status) && (error.status === 0 || error.status === -100);
}

function formatLogString(serviceName: string, level: string, datePipe: DatePipe, logArgs?: any[]): string {
    let logText = '';
    const tabChar = '\t';
    for (const log of logArgs) {
        try {
            logText += typeof log === 'string' ? log : JSON.stringify(log, null, tabChar);
            logText += ' ';
        } catch (e) {}
    }
    const logString = `${datePipe.transform(new Date(), 'yyyy-MM-dd HH:mm:ss.SSS')} ${[
        level
    ]}  ${serviceName}  ${logText}`;
    return logString;
}

function base64Encode(url: string, unescape?: boolean, encodeComponent?: boolean) {
    if (encodeComponent) {
        url = (window as any).encodeURIComponent(url);
    }
    if (unescape) {
        url = (window as any).unescape(url);
    }
    return window.btoa(url);
}

function isBase64(str: string) {
    if (str === '' || str.trim() === '') {
        return false;
    }
    try {
        return btoa(atob(str)) === str;
    } catch (err) {
        return false;
    }
}

/*
    Version 4 UUIDs have the form: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    where x is any hexadecimal digit and y is one of 8, 9, A, or B.
    e.g. f47ac10b-58cc-4372-a567-0e02b2c3d479

    source: http://en.wikipedia.org/wiki/Uuid#Definition
*/
function isValidType4UUID(str: string) {
    const regexExpForType4UUID =
        /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-4[a-f0-9]{3}\b-[89aAbB][a-f0-9]{3}\b-[0-9a-fA-F]{12}$/gi;
    if (regexExpForType4UUID.test(str)) {
        return true;
    }
    return false;
}

function cloneOrNull(object: any) {
    return object ? { ...object } : null;
}

function pick<T extends Object, K extends keyof T>(object: T, ...keys: K[]): Pick<T, K> {
    const result: Partial<Pick<T, K>> = {};
    for (const key of keys) {
        if (key in object) {
            result[key] = object[key];
        }
    }
    return result as Pick<T, K>;
}

function evalCss(expr: string) {
    // eslint-disable-next-line no-eval
    return eval(
        expr
            .trim()
            .replace(/calc/g, '')
            .replace(/px/g, '')
            .replace(/100vw/g, window.innerWidth.toString())
            .replace(/100vh/g, window.innerHeight.toString())
            .replace(/[^-()\d/*+.]/g, '') // strip anything but ()+-/* and digits
    );
}

function shallowCompare<T>(prev: T, curr: T): boolean {
    const prevKeys = Object.keys(prev);
    const currKeys = Object.keys(curr);
    if (prevKeys.length !== currKeys.length) {
        return false;
    }
    for (const key of prevKeys) {
        if (prev[key] !== curr[key]) {
            return false;
        }
    }
    return true;
}

function getDomainWithProtocol(url: string): string {
    try {
        const parsedUrl = new URL(url);
        return `${parsedUrl.origin}`;
    } catch (error) {
        return '';
    }
}

function mapScrimRGBToFontColor(scrimRGB: { r: number; g: number; b: number }): 'black' | 'white' {
    if (!scrimRGB) {
        return null;
    }
    const r = scrimRGB.r;
    const g = scrimRGB.g;
    const b = scrimRGB.b;
    const luma = r * 0.299 + g * 0.587 + b * 0.114;
    return luma > 150 ? 'black' : 'white';
}

function hexToRGB(hex: string): { r: number; g: number; b: number } {
    if (!new RegExp('^#(([0-9a-fA-F]{2}){3}|([0-9a-fA-F]){3})$').test(hex)) {
        return null;
    }

    let r, g, b;

    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else {
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
    }

    return { r, g, b };
}

/**
 * Converts a Map object to a plain JavaScript object
 * Useful for serializing Map objects for JSON.stringify
 *
 * @param mapObj Map to convert to plain object
 * @returns Plain JavaScript object with the same key-value pairs as the map
 */
function convertMapToObject<T>(mapObj: Map<string, T>): Record<string, T> {
    const obj = {};
    mapObj.forEach((value, key) => {
        obj[key] = value;
    });
    return obj;
}

/**
 * Flattens a nested object to dot notation for query parameters
 * Example: {cpu: {name: "Intel"}} becomes Map with "cpu.name" -> "Intel"
 *
 * @param obj Object to flatten
 * @param prefix Optional prefix for keys
 * @returns Map with dot notation keys and string values
 */
function flattenObjectToDotNotation(obj: any, prefix: string = ''): Map<string, string> {
    const flattened = new Map<string, string>();

    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            const newKey = prefix ? `${prefix}.${key}` : key;

            if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                // Recursively flatten nested objects
                const nestedMap = flattenObjectToDotNotation(value, newKey);
                nestedMap.forEach((nestedValue, nestedKey) => {
                    flattened.set(nestedKey, nestedValue);
                });
            } else {
                // Convert value to string for query parameters
                flattened.set(newKey, String(value));
            }
        }
    }

    return flattened;
}

export {
    normalizeUrl,
    toLowerCaseOrEmpty,
    generateUuid,
    invalidPipeArgumentError,
    getString,
    formatLogString,
    isTimeoutOrConnectivityError,
    base64Encode,
    cloneOrNull,
    pick,
    isValidType4UUID,
    isBase64,
    evalCss,
    shallowCompare,
    getDomainWithProtocol,
    mapScrimRGBToFontColor,
    hexToRGB,
    convertMapToObject,
    flattenObjectToDotNotation
};
